/**
 * Product image fallback — safe by construction.
 *
 * Problem context:
 *   `seed_products.py` shipped only THREE Unsplash photo IDs across 31
 *   products. Two of those IDs (and even the supposed "rod" id) actually
 *   render scuba/snorkelling/ocean photos on Unsplash today. We cannot
 *   guarantee any external photo ID without visually inspecting it.
 *
 * Decision:
 *   Stop falling back to ANY external photo for unverified products.
 *   Instead return `null` from `resolveProductImage()`, and let the
 *   renderer show a CSS-only gear-icon placeholder (gradient + lucide
 *   icon + category label). This is impossible to render the wrong
 *   subject by accident.
 *
 *   When a real product photo is uploaded to `product.image_url`, it
 *   wins as before.
 */

// Photo IDs known to render unrelated content (snorkelling / ocean / etc.).
// The Unsplash hosting can rotate the asset behind an ID over time, so we
// only honour an image_url if it's NOT in this denylist AND it isn't from
// Unsplash at all (real product uploads always pass).
const KNOWN_BAD_IDS = [
  'photo-1578662996442-48f60103fc96', // generic catch-all — snorkelling
  'photo-1556821840-3a63f95609a7',    // ocean
  'photo-1544551763-46a013bb70d5',    // labelled "rods" but actually a scuba shot
  'photo-1559827260-dc66d52bef19',    // labelled "reels" — unverified
];

const isBadStockUrl = (url) => {
  if (!url || typeof url !== 'string') return true;
  return KNOWN_BAD_IDS.some((id) => url.includes(id));
};

/**
 * Resolve the final image URL for a product.
 *
 * Returns:
 *   • a usable URL string when product has a real (non-deny-listed) image
 *   • null when the renderer should show the CSS placeholder
 *
 * The renderer should check `productHasImage(product)` and either render
 * <img src={resolveProductImage(product)} /> or a `<ProductImagePlaceholder />`.
 */
export const resolveProductImage = (product) => {
  if (!product) return null;
  const url = product.image_url || (Array.isArray(product.images) ? product.images[0] : null);
  if (!url || isBadStockUrl(url)) return null;
  return url;
};

export const productHasImage = (product) => resolveProductImage(product) !== null;
